import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useStoreState, Session } from "ezStore";

import { useQuery } from "@apollo/client";

import { Button, Card, FormGroup, PageHeader } from "@cofano/cck";
import { Box, CircularProgress, Grid2 as Grid, Tab, Tabs, Typography, useTheme } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import ContractStatusChanger from "components/contract/ContractStatusChanger";
import useTabs from "components/hooks/useTabs";
import Documents from "components/documents/Documents";
import { entityWithDocument, openSidebarWithPdf } from "components/pdf/PdfContainer";
import useLayout from "components/layout/useLayout";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";

import { DataFormat, Fields, hasOptionalFieldPermission, Language, usePermission } from "imports/Utils";

import { CONTRACT_DETAIL_QUERY } from "./utils/ContractQueries";
import {
	ContractDetailsQuery,
	DocumentEntity,
} from "__generated__/graphql";

import CharterList from "./sections/CharterList";
import SendContractDialog from "view/contractsNew/components/dialogs/SendContractDialog";
import ContractHistoryDialog from "view/contractsNew/components/dialogs/ContractHistoryDialog";
import EntityHistoryDialog from "components/entityHistory/components/dialogs/EntityHistoryDialog";

import { ChildProps, CreateEditEntityInDialogProps } from "types/commons";

import ContractOptionDetails from "view/contractsNew/components/details/ContractOptionDetails";
import DuplicateContractDialog from "view/contractsNew/components/dialogs/DuplicateContractDialog";
import LabelTag from "view/labels/components/tags/LabelTag";
import ChildContractList from "view/contractsNew/sections/ChildContractList";
import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";

import { ContractLink, LocationLink, RelationLink } from "utils/LinkUtils";
import { FormattedDateWindow } from "utils/DateUtils";

import "./styling/styling.scss";
import AgreementDetails from "view/agreements/AgreementDetails";
import { getAgreements_query } from "view/agreements/utils/AgreementQueries";
import { CharterSubtype, CharterType, HistoryEntity } from "__generated__/enums";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import { parentContractCompatibleSubtypes } from "view/contractsNew/utils/ContractUtils";

type Contract = ChildProps<ContractDetailsQuery, "contract">

const TABS = {
	AGREEMENTS: "agreements",
	DOCUMENTS: "documents",
	OPTIONS: "options",
};

const ContractDetails = ({ pageInDialogSettings }: { pageInDialogSettings?: CreateEditEntityInDialogProps }) => {

	const params = useParams();
	const id = pageInDialogSettings ? pageInDialogSettings.entityId : params.id;

	const navigate = useNavigate();
	const { isReadOnly, isRTR, isFinanceAdmin, isMainUserLimited, isAdmin, isCompanyUser, isCustomer, isOperator } = usePermission();
	const canReadDocuments = isRTR || isCustomer || isOperator;
	const layout = useLayout();
	const session = useStoreState(store => store.session);

	const theme = useTheme();

	const [ duplicateDialogOpen, openDuplicateDialog ] = useState<boolean>(false);
	const [ sendDialogOpen, openSendDialog ] = useState<boolean>(false);
	const [ historyDialogOpen, openHistoryDialog ] = useState<boolean>(false);
	const [ changeHistoryDialogOpen, openChangeHistoryDialog ] = useState<boolean>(false);

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: TABS.AGREEMENTS,
		disableParams: !!pageInDialogSettings,
	});

	const { data, loading, refetch } = useQuery(CONTRACT_DETAIL_QUERY, {
		variables: {
			id: Number(id),
		},
	});

	const { data: agreementsData, loading: loadingAgreements } = useQuery(getAgreements_query, {
		variables: {
			search: {
				contractId: Number(id),
			},
		},
		skip: !id,
	});

	const contract = data?.contract;

	const { commentsProps } = useComments({
		collapsableDisabled: true,
		contracts: contract ? [ { ...contract, contractNumber: contract.contractNumber ?? "" } ] : [],
	});

	const getContractPdf = () => {
		openSidebarWithPdf(entityWithDocument.CONTRACT, contract?.id, layout);
	};

	const isSaleContract = contract?.charterType === CharterType.Sales;
	const isParentContract = contract?.isParentContract != null && contract.isParentContract;
	const canHaveCharters = !isParentContract || contract?.charterSubtype === CharterSubtype.Time; // parent + time can have a charter

	return (
		<div className="contract-details">
			{!pageInDialogSettings && (
				<PageHeader>
					<PageHeader.Title>
						{Language("CONTRACT")}
						<Helmet title={intl.formatMessage({ id: "CONTRACT" })} />
					</PageHeader.Title>

					<PageHeader.Actions>
						<Button onClick={() => navigate("/view/contracts")} variant="outlined">
							{intl.formatMessage({ id: "CONTRACTS" })}
						</Button>

						{!isReadOnly && (isRTR || isFinanceAdmin) && !isSaleContract && (
							<Button onClick={() => openDuplicateDialog(true)} variant="outlined">
								{intl.formatMessage({ id: "DUPLICATE" })}
							</Button>
						)}
						<Button onClick={() => getContractPdf()} disabled={!contract} variant="outlined">
							PDF
						</Button>

						<Button onClick={() => openSendDialog(true)} disabled={!contract} variant="outlined">
							{intl.formatMessage({ id: "SEND" })}
						</Button>

						<Button onClick={() => openHistoryDialog(true)} disabled={!contract} variant="outlined">
							{intl.formatMessage({ id: "SEND_HISTORY" })}
						</Button>

						{isRTR && !isMainUserLimited && (
							<Button onClick={() => openChangeHistoryDialog(true)} disabled={!contract} variant="outlined">
								{intl.formatMessage({ id: "ENTITY_HISTORY" })}
							</Button>
						)}

						{!isReadOnly && (isRTR || isFinanceAdmin) && contract && contract.isEditable && (
							<Button onClick={() => navigate(`/view/contracts/${id}/edit`)}>
								{intl.formatMessage({ id: "EDIT" })}
							</Button>
						)}
					</PageHeader.Actions>
				</PageHeader>
			)}

			{(loading || !contract) ? (
				<CircularProgress size={24} />
			) : (
				<Grid spacing={3} container>
					<Grid size={5}>
						<Card>
							<ContractDetailFields
								contract={contract}
								refetch={refetch}
								session={session}
							/>
						</Card>

						{!pageInDialogSettings && (
							<>
								{canHaveCharters && (
									<div style={{ marginTop: 16 }}>
										<CharterList
											contract={contract}
											refetch={refetch}
										/>
									</div>
								)}

								{isParentContract && (
									<div style={{ marginTop: 16 }}>
										<ChildContractList
											contract={contract}
											refetch={refetch}
										/>
									</div>
								)}
							</>
						)}
					</Grid>

					<Grid size={7}>
						<Card disablePadding>
							<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
								<Tabs
									variant='scrollable'
									value={selectedTab}
									onChange={(_event, newValue) => selectTab(newValue)}>
									<Tab label={Language("AGREEMENTS")} value={TABS.AGREEMENTS} />

									{!pageInDialogSettings && canReadDocuments && (
										<Tab label={Language("DOCUMENTS")} value={TABS.DOCUMENTS} />
									)}

									{hasOptionalFieldPermission(session, Fields.CONTRACT, "contractOptions") && contract.charterType === CharterType.Purchase && contract.charterSubtype === CharterSubtype.Time && (
										<Tab label={Language("CONTRACT_OPTIONS")} value={ TABS.OPTIONS } />
									)}
								</Tabs>
							</Box>

							{selectedTab === TABS.AGREEMENTS && (
								<div style={{ backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#F0F0F0" }}>
									<AgreementDetails
										preobtainedAgreements={{
											queryData: agreementsData?.agreements ?? [],
											loading: loadingAgreements,
										}}
										charterType={contract.charterType}
										charterSubtype={contract.charterSubtype}
										currency={contract.currency || { isoCode: "EUR", symbol: "€" }}
									/>
								</div>
							)}

							{selectedTab === TABS.DOCUMENTS && canReadDocuments && (
								<React.Fragment>
									<Documents
										entities={[ {
											id: contract.id,
											name: contract.contractNumber || "",
											type: DocumentEntity.Contract,
										} ]}
										canCreate={!isReadOnly && (isAdmin || isCompanyUser)} />
								</React.Fragment>
							)}

							{selectedTab === TABS.OPTIONS &&
								<ContractOptionDetails
									contract={{
										...contract,
										agreements: agreementsData?.agreements ?? [],
									}}
									refetch={refetch}
								/>
							}
						</Card>
					</Grid>
				</Grid>
			)}

			{contract && (
				<>
					{!isSaleContract && duplicateDialogOpen &&
						<DuplicateContractDialog
							open={duplicateDialogOpen}
							onClose={() => openDuplicateDialog(false)}
							contract={contract}
						/>
					}
					<SendContractDialog
						open={sendDialogOpen}
						onClose={() => openSendDialog(false)}
						contract={contract}
						refetch={refetch} />
				</>
			)}

			<ContractHistoryDialog
				open={historyDialogOpen}
				onClose={() => openHistoryDialog(false)}
				history={contract?.sendHistoryItems ?? []}
			/>

			{contract && (
				<EntityHistoryDialog
					open={changeHistoryDialogOpen}
					onClose={() => openChangeHistoryDialog(false)}
					entity={HistoryEntity.Contract}
					entityId={contract.id}
					title={`${intl.formatMessage({ id: "ENTITY_HISTORY" })}${contract.contractNumber ? ` - ${contract.contractNumber}` : ""}`}
				/>
			)}

			{!pageInDialogSettings && (
				<CommentsWidget {...commentsProps} />
			)}
		</div>
	);
};

export const ContractDetailFields = ({ contract, refetch, session }: { contract: Contract, refetch: () => void, session: Session }) => {
	const isTimePurchaseContract = contract.charterType === CharterType.Purchase && contract.charterSubtype === CharterSubtype.Time;
	const { isReadOnly, isRTR, isFinanceAdmin } = usePermission();

	return (
		<React.Fragment>
			<FormGroup label={Language("CONTRACT_NUMBER")} labelPlacement="left-center" overflow="hidden">
				{contract.contractNumber}
			</FormGroup>

			<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
				<CharterTypeTag type={contract.charterType} />
			</FormGroup>

			<FormGroup label={Language("STATUS")} labelPlacement="left-center" overflow="hidden">
				{contract.status && (
					<ContractStatusChanger
						contract={contract}
						refetch={refetch}
						disabled={isReadOnly || !(isRTR || isFinanceAdmin)}
					/>
				)}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "statusUpdatedOn") && (
				<FormGroup label={Language("CONTRACT_STATUS_UPDATED_ON")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.timeSinceDate(contract.statusUpdatedOn)}
				</FormGroup>
			)}

			<FormGroup label={Language("CHARTER_TYPE")} labelPlacement="left-center" overflow="hidden">
				<CharterSubtypeTag type={contract.charterSubtype} />
			</FormGroup>

			<FormGroup label={Language("DESCRIPTION")} labelPlacement="left-center" overflow="hidden">
				{contract.description}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "memo") && (
				<FormGroup label={Language("CONTRACT_MEMO")} labelPlacement="left-center" overflow="hidden">
					{contract.memo}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "labels") && (
				<FormGroup
					label={Language("LABELS")}
					labelPlacement="left"
					labelPaddingTop={4}
					overflow="hidden">
					{contract.labels && contract.labels.map((label) => label && (
						<LabelTag key={label.id} label={label} />
					))}
				</FormGroup>
			)}

			<FormGroup label={Language("CONTRACT_CURRENCY")} labelPlacement="left-center" overflow="hidden">
				{contract.currency?.isoCode}
			</FormGroup>

			<FormGroup label={Language("INVOICE_CREDITOR")} labelPlacement="left-center" overflow="hidden">
				<RelationLink relation={contract.creditor} />
			</FormGroup>

			{contract.creditorContact && (
				<FormGroup label={Language("CONTACT_PERSON")} labelPlacement="left-center" overflow="hidden">
					{contract.creditorContact.name}
				</FormGroup>
			)}

			<FormGroup label={Language("INVOICE_DEBTOR")} labelPlacement="left-center" overflow="hidden">
				<RelationLink relation={contract.debtor} />
			</FormGroup>

			{contract.debtorContact && (
				<FormGroup label={Language("CONTACT_PERSON")} labelPlacement="left-center" overflow="hidden">
					{contract.debtorContact.name}
				</FormGroup>
			)}

			<FormGroup label={Language("BROKER")} labelPlacement="left-center" overflow="hidden">
				<RelationLink relation={contract.broker} />
			</FormGroup>


			<FormGroup label={Language("VALID_BETWEEN")} labelPlacement="left-center" overflow="hidden">
				{contract.validFrom ? <FormattedDateWindow from={contract.validFrom} to={contract.validUntil} format="ll" /> : ""}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "noticePeriod") && (
				<FormGroup label={Language("CONTRACT_NOTICE_PERIOD")} labelPlacement="left-center" overflow="hidden">
					{contract.noticePeriod && intl.formatMessage({ id: `CONTRACT_NOTICE_PERIOD_${contract.noticePeriod}` })}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "versionNumber") && (
				<FormGroup label={Language("VERSION_NUMBER")} labelPlacement="left-center" overflow="hidden">
					{contract.versionNumber}
				</FormGroup>
			)}

			{isTimePurchaseContract && hasOptionalFieldPermission(session, Fields.CONTRACT, "allIn") && (
				<FormGroup label={Language("ALL_IN")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(contract.allIn)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "regions")
				&& isTimePurchaseContract && (
				<FormGroup
					label={Language("REGIONS")}
					labelPlacement="left"
					labelPaddingTop={0}
					overflow="hidden">
					{contract.regions?.map((r, index) =>
						<div key={index}><LocationLink location={r} /></div>)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "productCategory")
				&& isTimePurchaseContract && (
				<FormGroup
					label={Language("PRODUCT_CATEGORY")}
					labelPlacement="left-center"
					labelPaddingTop={0}
					overflow="hidden">
					{contract.productCategory?.name}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "contractOptions")
				&& isTimePurchaseContract
				&& contract.originalContractForOption &&
				<FormGroup label={Language("ORIGINAL_CONTRACT_FOR_OPTION")} labelPlacement="left-center" overflow="hidden">
					<ContractLink contract={contract.originalContractForOption} />
				</FormGroup>
			}

			{hasOptionalFieldPermission(session, Fields.CONTRACT, "contractOptions")
				&& isTimePurchaseContract
				&& contract.takenOptionContract &&
				<FormGroup label={Language("TAKEN_OPTION_CONTRACT")} labelPlacement="left-center" overflow="hidden">
					<ContractLink contract={contract.takenOptionContract} />
				</FormGroup>
			}

			{contract.backToBackContract &&
				<FormGroup label={Language("BACK_TO_BACK")} labelPlacement="left-center" overflow="hidden">
					<ContractLink contract={contract.backToBackContract} />
				</FormGroup>
			}

			<FormGroup label={Language("INCLUDE_PRICING_IN_NOMINATION")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(contract.includePricingInNomination)}
			</FormGroup>

			{contract.termsAndConditions && (
				<FormGroup label={Language("TERMS_AND_CONDITIONS")} labelPlacement="left-center" overflow="hidden">
					<Typography
						sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
						{contract.termsAndConditions.description}
					</Typography>
				</FormGroup>
			)}

			{parentContractCompatibleSubtypes.includes(contract.charterSubtype) && hasOptionalFieldPermission(session, Fields.CONTRACT, "parentContract") && (
				<React.Fragment>
					{!contract.parentContract && (
						<FormGroup label={Language("IS_PARENT_CONTRACT")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.boolean(contract.isParentContract)}
						</FormGroup>
					)}

					{contract.parentContract && (
						<FormGroup label={Language("PARENT_CONTRACT")} labelPlacement="left-center" overflow="hidden">
							<ContractLink contract={contract.parentContract} />
						</FormGroup>
					)}
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default ContractDetails;
